import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Palette } from "lucide-react";

const StyleGuide = () => {
  const colors = [
    { name: "Primary Navy", hex: "#141b2d", usage: "Background principal (dark)" },
    { name: "Card Background", hex: "#1a2332", usage: "Cards, modales, popovers (dark)" },
    { name: "Border", hex: "#2a3444", usage: "Bordes, separadores (dark)" },
    { name: "Gold / Accent", hex: "#D4AF37", usage: "Botones principales, acentos, iconos" },
    { name: "Text Primary", hex: "#ffffff", usage: "Texto principal (dark)" },
    { name: "Text Secondary", hex: "#9ca3af", usage: "Texto secundario, labels" },
    { name: "Text Muted", hex: "#6b7280", usage: "Texto terciario, placeholders" },
    { name: "Income Green", hex: "#22c55e", usage: "Ingresos, positivos, ahorro" },
    { name: "Expense Red", hex: "#ef4444", usage: "Gastos, negativos, deudas vencidas" },
    { name: "Warning Amber", hex: "#f59e0b", usage: "Alertas, pendientes" },
    { name: "Info Blue", hex: "#3b82f6", usage: "Ahorro en charts, links" },
    { name: "Purple", hex: "#8b5cf6", usage: "Deudas en dashboard" },
    { name: "Streak Orange", hex: "#f97316", usage: "Racha de uso" },
    { name: "Light BG", hex: "#f7f7f7", usage: "Background principal (light)" },
    { name: "Light Card", hex: "#ffffff", usage: "Cards (light)" },
    { name: "Light Border", hex: "#e5e7eb", usage: "Bordes (light)" },
    { name: "Light Text", hex: "#1a1a2e", usage: "Texto principal (light)" },
  ];

  const fonts = [
    { name: "Playfair Display", usage: "Titulos principales, nombres de seccion", weight: "700 (Bold)", sizes: "text-base a text-2xl" },
    { name: "Epilogue", usage: "Subtitulos, headers de cards en Reportes", weight: "600 (Semibold)", sizes: "text-base a text-xl" },
    { name: "System Mono", usage: "Montos, cifras financieras", weight: "500-700", sizes: "text-xs a text-3xl" },
    { name: "System Sans (Inter/default)", usage: "Texto general, labels, botones", weight: "400-600", sizes: "text-xs a text-base" },
  ];

  const hierarchy = [
    { level: "H1", class: "text-2xl sm:text-3xl", usage: "Titulo de pagina (Dashboard, Reportes, etc.)" },
    { level: "H2 / Card Title", class: "text-base", usage: "Titulo de card, secciones" },
    { level: "Body", class: "text-sm", usage: "Texto general, descripciones" },
    { level: "Small / Label", class: "text-xs", usage: "Labels de formularios, subtexto" },
    { level: "Micro", class: "text-[10px]", usage: "Badges, estados, notas al pie" },
  ];

  return (
    <Card className="bg-[#1a2332] border-[#2a3444]" data-testid="style-guide">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
          <Palette className="w-5 h-5 text-[#D4AF37]" />
          Guia de Estilo — LD Finance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Colors */}
        <div>
          <h3 className="text-sm font-semibold text-[#D4AF37] mb-3">Paleta de Colores</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {colors.map(c => (
              <div key={c.hex} className="flex items-center gap-2 p-2 rounded-lg bg-[#141b2d] border border-[#2a3444]">
                <div className="w-8 h-8 rounded shrink-0 border border-[#2a3444]" style={{ backgroundColor: c.hex }} />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-white truncate">{c.name}</p>
                  <p className="text-[10px] font-mono text-[#D4AF37]">{c.hex}</p>
                  <p className="text-[9px] text-gray-500 truncate">{c.usage}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Typography */}
        <div>
          <h3 className="text-sm font-semibold text-[#D4AF37] mb-3">Tipografias</h3>
          <div className="space-y-2">
            {fonts.map(f => (
              <div key={f.name} className="p-3 rounded-lg bg-[#141b2d] border border-[#2a3444]">
                <p className="text-sm text-white font-medium" style={{ fontFamily: f.name === 'System Mono' ? 'monospace' : f.name }}>{f.name}</p>
                <p className="text-[10px] text-gray-400">Peso: {f.weight} | Tamaños: {f.sizes}</p>
                <p className="text-[10px] text-gray-500">{f.usage}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Hierarchy */}
        <div>
          <h3 className="text-sm font-semibold text-[#D4AF37] mb-3">Jerarquia de Texto</h3>
          <div className="space-y-1.5">
            {hierarchy.map(h => (
              <div key={h.level} className="flex items-center gap-3 p-2 rounded bg-[#141b2d] border border-[#2a3444]">
                <span className="text-xs font-mono text-[#D4AF37] w-20 shrink-0">{h.level}</span>
                <span className="text-xs font-mono text-gray-500 w-32 shrink-0">{h.class}</span>
                <span className="text-xs text-gray-400 truncate">{h.usage}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Component Patterns */}
        <div>
          <h3 className="text-sm font-semibold text-[#D4AF37] mb-3">Componentes Clave</h3>
          <div className="space-y-1.5 text-xs text-gray-400">
            <p><span className="text-white">Botón principal:</span> .btn-gold — bg-[#D4AF37] text-[#141b2d] rounded-md</p>
            <p><span className="text-white">Cards:</span> bg-[#1a2332] border-[#2a3444] rounded-lg</p>
            <p><span className="text-white">Inputs:</span> bg-[#141b2d] border-[#2a3444] text-white</p>
            <p><span className="text-white">Badges positivos:</span> bg-green-500/10 text-green-400 rounded-full</p>
            <p><span className="text-white">Badges negativos:</span> bg-red-500/10 text-red-400 rounded-full</p>
            <p><span className="text-white">Badges neutros:</span> bg-[#D4AF37]/10 text-[#D4AF37] rounded-full</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StyleGuide;
